import { TimesheetBalanceFragment } from 'generated/resources/graphql'

export type DailyTimeCategory = {
  date: string
  timeCategories: {
    name: string
    minutes: number
  }[]
}

type Transfer = TimesheetBalanceFragment['balancePools'][number]['transfers'][number]

type GroupedTransfer = {
  name: string
  totalMinutes: number
}

const createCompositeKey = (transfer: Transfer): string => {
  return [
    transfer.timeSettingsCustomTimeRangeCategoryId ?? 'null',
    transfer.rawMinutesInCents,
    transfer.equivalentMinutesInCents,
  ].join('_')
}

const collectValidTransfers = (balance: TimesheetBalanceFragment): Transfer[] => {
  return balance.balancePools.flatMap((balancePool) =>
    balancePool.transfers.filter((transfer) => !!transfer.name)
  )
}

const groupTransfersByCompositeKey = (transfers: Transfer[]): Record<string, GroupedTransfer> => {
  return transfers.reduce<Record<string, GroupedTransfer>>((acc, transfer) => {
    const compositeKey = createCompositeKey(transfer)

    if (!acc[compositeKey]) {
      acc[compositeKey] = {
        name: transfer.name ?? '',
        totalMinutes: 0,
      }
    }

    acc[compositeKey].totalMinutes += Number(transfer.minutes)
    return acc
  }, {})
}

const convertToTimeCategories = (
  groupedTransfers: Record<string, GroupedTransfer>
): DailyTimeCategory['timeCategories'] => {
  return Object.values(groupedTransfers).map((group) => ({
    name: group.name,
    minutes: group.totalMinutes,
  }))
}

const isValidBalance = (balance: TimesheetBalanceFragment): boolean => {
  return !!balance.date && balance.balancePools.length > 0
}

export const generateDailyTimeCategories = (
  balances: TimesheetBalanceFragment[]
): DailyTimeCategory[] => {
  return balances
    .filter(isValidBalance)
    .map((balance) => {
      const transfers = collectValidTransfers(balance)
      const groupedTransfers = groupTransfersByCompositeKey(transfers)
      const timeCategories = convertToTimeCategories(groupedTransfers)

      return {
        date: balance.date,
        timeCategories,
      }
    })
    .filter((balance) => balance.timeCategories.length > 0)
}
